import { createFileRoute, notFound } from '@tanstack/react-router';

import { DuoVideoToolPage } from '@/blocks/ai-tools/duo-video-tool-page';
import { PhotoStyleToolPage } from '@/blocks/ai-tools/photo-style-tool-page';
import { getAiTool, localized, toolAssetUrl, type AiToolDefinition } from '@/config/ai-tools';
import { buildHead } from '@/lib/seo';
import type { SlotEntry } from '@/lib/store/recommendation-slot';
import type { ToolSummary } from '@/modules/directory/types';
import { getAiToolPageData } from '@/server-functions/ai-tools';

export const Route = createFileRoute('/(store)/tools/$slug')({
  loader: async ({ params }) => {
    const data = await getAiToolPageData({ data: { slug: params.slug } });
    if (!data.found) throw notFound();
    return data;
  },
  head: ({ loaderData, params }) => {
    const tool = getAiTool(params.slug);
    if (!loaderData || !tool) return {};
    const locale = loaderData.locale;
    const shareImage = tool.socialImage ?? tool.ogImage;
    return buildHead({
      locale,
      title: localized(tool.seoTitle, locale),
      description: localized(tool.seoDescription, locale),
      keywords: localized(tool.keywords, locale),
      path: `/tools/${tool.slug}`,
      // og:image must be absolute.
      image: shareImage ? toolAssetUrl(shareImage) : undefined,
    });
  },
  component: ToolPage,
});

/** One page block per engine; a new `kind` fails typecheck here until it has one. */
function EnginePage({
  tool,
  slot,
  recentRenders,
}: {
  tool: AiToolDefinition;
  slot: SlotEntry<ToolSummary>[];
  recentRenders: number | null;
}) {
  switch (tool.kind) {
    case 'duo-video':
      return <DuoVideoToolPage tool={tool} slot={slot} recentRenders={recentRenders} />;
    case 'photo-to-photo':
    case 'photo-video':
      return <PhotoStyleToolPage tool={tool} slot={slot} recentRenders={recentRenders} />;
    default: {
      // `tool` is `never` here once every kind has a case; the cast only reads its kind for the message.
      const unhandled: never = tool;
      throw new Error(`No page for AI tool kind ${String((unhandled as AiToolDefinition).kind)}`);
    }
  }
}

function ToolPage() {
  const { slug } = Route.useParams();
  const { slot, recentRenders } = Route.useLoaderData();
  const tool = getAiTool(slug);
  if (!tool) return null;
  return <EnginePage tool={tool} slot={slot} recentRenders={recentRenders} />;
}
